import React, { useState, useEffect } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";

const GroupVersionDetails = ({ isLoading, isError, onFormChange, formData, formErrors, groups }) => {
  const [groupDetails, setGroupDetails] = useState({
    location: "",
    segment: "",
    assetType: ""
  });
  const [availableYears, setAvailableYears] = useState([]);
  const [existingYears, setExistingYears] = useState([]);

  const ogmpLevelOptions = [
    { value: "L3", label: "L3" },
    { value: "L3/L4", label: "L3/L4" },
    { value: "L3/L4/L5", label: "L3/L4/L5" },
    { value: "L4", label: "L4" },
    { value: "L4/L5", label: "L4/L5" },
    { value: "L5", label: "L5" }
  ];

  const unitOptions = [
    { value: "kgch4", label: "kgCH4" },
    { value: "tch4", label: "tCH4" }
  ];

  const groupOptions =
    groups?.map((group) => ({
      value: group.id,
      label: group.name
    })) || [];

  useEffect(() => {
    if (formData.groupId && groups) {
      const selectedGroup = groups.find((group) => group.id === formData.groupId);
      if (selectedGroup) {
        setGroupDetails({
          location: selectedGroup.location || "N/A",
          segment: selectedGroup.segment || "N/A",
          assetType: selectedGroup.assetType || "N/A"
        });

        // track existing OGMP years for the selected group
        const groupYears = selectedGroup.ogmpVersions || [];
        setExistingYears(groupYears.map((year) => year.toString()));
      } else {
        setGroupDetails({
          location: "",
          segment: "",
          assetType: ""
        });
        setExistingYears([]);
      }
    } else {
      setGroupDetails({
        location: "",
        segment: "",
        assetType: ""
      });
      setExistingYears([]);
    }
  }, [formData.groupId, groups]);

  // set default productionCh4Unit to kgCH4 when component mounts
  useEffect(() => {
    if (!formData.productionCh4Unit) {
      onFormChange("productionCh4Unit", "kgch4");
    }
  }, [formData.productionCh4Unit, onFormChange]);

  // generate years for dropdown (from 2014 until the current year + 1)
  const currentYear = new Date().getFullYear();
  const startYear = 2014;
  const years = Array.from({ length: currentYear - startYear + 2 }, (_, i) => startYear + i);

  useEffect(() => {
    // update available year options based on existing years
    const allYearOptions = years.map((year) => ({
      value: year.toString(),
      label: year.toString(),
      isDisabled: existingYears.includes(year.toString())
    }));
    setAvailableYears(allYearOptions);
  }, [existingYears, years]);

  // fix infinite rendering issue. separate useEffect to avoid circular dependency
  useEffect(() => {
    // only check for existing years after initial render, and avoid triggering a change
    if (formData.ogmpYear && existingYears.includes(formData.ogmpYear)) {
      // we'll just mark the validation in the parent component instead of triggering onFormChange
      // this breaks the circular dependency
    }
  }, [existingYears, formData.ogmpYear]);

  const handleFieldChange = (e) => {
    const { id, value } = e.target;
    onFormChange(id, value);
  };

  const handleSelectChange = (id, selectedOption, hasError = false) => {
    // Pass the value and error state
    onFormChange(id, selectedOption?.value || "", hasError);
  };

  const handleGroupChange = (option) => {
    handleSelectChange("groupId", option);
    // reset ogmpYear when group changes to avoid showing validation errors immediately
    onFormChange("ogmpYear", "");

    // find the selected group to populate the related fields
    if (option && groups) {
      const selectedGroup = groups.find((group) => group.id === option.value);
      if (selectedGroup) {
        setGroupDetails({
          location: selectedGroup.location || "N/A",
          segment: selectedGroup.segment || "N/A",
          assetType: selectedGroup.assetType || "N/A"
        });

        // set existing years from the selected group
        const groupYears = selectedGroup.ogmpVersions || [];
        setExistingYears(groupYears.map((year) => year.toString()));
      }
    } else {
      setGroupDetails({
        location: "",
        segment: "",
        assetType: ""
      });
      setExistingYears([]);
    }
  };

  const handleYearChange = (option) => {
    const yearExists = existingYears.includes(option?.value);
    handleSelectChange("ogmpYear", option, yearExists);
  };

  return (
    <div className="project-group-section mb-5 p-4 bg-light border rounded" style={{ maxHeight: "600px", overflowY: "scroll" }}>
      <h5 className="text-white p-2" style={{ backgroundColor: "#243544" }}>
        Group Version Details
      </h5>

      {/* Project Group Selection */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="groupId" className="mb-4">
            <Form.Label>
              Project Group <span className="text-danger">*</span>
            </Form.Label>
            <div>
              <Select
                options={groupOptions}
                onChange={handleGroupChange}
                value={groupOptions.find((option) => option.value === formData.groupId) || null}
                placeholder="Select Project Group"
                isClearable
                isSearchable
                className={formErrors?.groupId ? "is-invalid" : ""}
              />
              {formErrors?.groupId && <div className="invalid-feedback d-block">{formErrors.groupId}</div>}
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="group_location" className="mb-4">
            <Form.Label>Group Location</Form.Label>
            <Form.Control
              type="text"
              placeholder="Location (readonly)"
              value={groupDetails.location}
              disabled
              readOnly
              className="bg-light"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Group Details */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="group_segment" className="mb-4">
            <Form.Label>Group Segment</Form.Label>
            <Form.Control
              type="text"
              placeholder="Segment (readonly)"
              value={groupDetails.segment}
              disabled
              readOnly
              className="bg-light"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="group_assetType" className="mb-4">
            <Form.Label>Group Asset Type</Form.Label>
            <Form.Control
              type="text"
              placeholder="Asset Type (readonly)"
              value={groupDetails.assetType}
              disabled
              readOnly
              className="bg-light"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* OGMP Info */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="ogmpYear" className="mb-4">
            <Form.Label>
              OGMP Year <span className="text-danger">*</span>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="info-tooltip" className="text-start">
                    The ogmp year is unique for each project group. To change it, delete and create a new version.
                  </Tooltip>
                }
              >
                <span style={{ cursor: "pointer", marginLeft: "5px" }}>
                  <FontAwesomeIcon icon={faInfoCircle} className="text-primary" style={{ fontSize: "16px" }} />
                </span>
              </OverlayTrigger>
            </Form.Label>
            <div>
              <Select
                options={availableYears}
                onChange={handleYearChange}
                value={availableYears.find((option) => option.value === formData.ogmpYear) || null}
                placeholder="Select Year"
                isClearable
                isSearchable
                className={formErrors?.ogmpYear ? "is-invalid" : ""}
              />
              {formErrors?.ogmpYear && <div className="invalid-feedback d-block">{formErrors.ogmpYear}</div>}
              {existingYears.includes(formData.ogmpYear) && (
                <div className="invalid-feedback d-block">This group already has a version for {formData.ogmpYear}</div>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="ogmpLevel" className="mb-4">
            <Form.Label>
              OGMP Level <span className="text-danger">*</span>
            </Form.Label>
            <div>
              <Select
                options={ogmpLevelOptions}
                onChange={(option) => handleSelectChange("ogmpLevel", option)}
                value={ogmpLevelOptions.find((option) => option.value === formData.ogmpLevel) || null}
                placeholder="Select OGMP Level"
                isClearable
                isSearchable
                className={formErrors?.ogmpLevel ? "is-invalid" : ""}
              />
              {formErrors?.ogmpLevel && <div className="invalid-feedback d-block">{formErrors.ogmpLevel}</div>}
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Site Surveys and Production CH4 */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="plannedSiteSurveys" className="mb-4">
            <Form.Label>Planned Site Surveys</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Planned Number of Site Surveys"
              value={formData.plannedSiteSurveys}
              onChange={handleFieldChange}
              min="0"
              max="5000"
              step="1"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="uncertaintyFraction" className="mb-4">
            <Form.Label>
              Uncertainty Fraction <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Uncertainty Fraction (e.g. 0.25 for 25%)"
              value={formData.uncertaintyFraction}
              onChange={handleFieldChange}
              min="0"
              max="1"
              step="0.01"
              className={formErrors?.uncertaintyFraction ? "is-invalid" : ""}
            />
            {formErrors?.uncertaintyFraction && <div className="invalid-feedback d-block">{formErrors.uncertaintyFraction}</div>}
          </Form.Group>
        </Col>
      </Row>

      {/* Unit and Uncertainty */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="productionCh4" className="mb-4">
            <Form.Label>
              Production CH4 <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Production CH4 (for the OGMP year)"
              value={formData.productionCh4}
              onChange={handleFieldChange}
              min="0"
              max="50000000"
              step="1000"
              className={formErrors?.productionCh4 ? "is-invalid" : ""}
            />
            {formErrors?.productionCh4 && <div className="invalid-feedback d-block">{formErrors.productionCh4}</div>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="productionCh4Unit" className="mb-4">
            <Form.Label>
              Production CH4 Unit <span className="text-danger">*</span>
            </Form.Label>
            <div>
              <Select
                options={unitOptions}
                onChange={(option) => handleSelectChange("productionCh4Unit", option)}
                value={unitOptions.find((option) => option.value === formData.productionCh4Unit) || null}
                placeholder="Select Unit"
                defaultValue={unitOptions[0]}
                isClearable
                isSearchable
                className={formErrors?.productionCh4Unit ? "is-invalid" : ""}
              />
              {formErrors?.productionCh4Unit && <div className="invalid-feedback d-block">{formErrors.productionCh4Unit}</div>}
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Equity Information */}
      <Row>
        <Col md={6}>
          <Form.Group controlId="typeOfEquity" className="mb-4">
            <Form.Label>Type of Equity</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Type of Equity (e.g. Common Stock)"
              value={formData.typeOfEquity}
              onChange={handleFieldChange}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="equityFraction" className="mb-4">
            <Form.Label>Equity Fraction</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Equity Fraction (e.g. 0.5 for 50%)"
              value={formData.equityFraction}
              onChange={handleFieldChange}
              min="0"
              max="1"
              step="0.01"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Comments */}
      <Row>
        <Col md={12}>
          <Form.Group controlId="comments" className="mb-4">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              placeholder="Enter any additional comments"
              value={formData.comments}
              onChange={handleFieldChange}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default GroupVersionDetails;
